'use client';

import { cn } from '@/lib/utils';

import type { Service } from '../../types/meetings.type';
import type { Product, ProductWithCategory } from '../../types/product.types';
import Accordion from '../Accordion';

type Props = {
  className?: string;
  products: Array<ProductWithCategory>;
  services: Array<Service>;
  onSelect: (service: Service, product?: Product) => void;
};

const ProductSelection = ({ className = '', onSelect, products, services }: Props) => {
  const productsByCategory = products.reduce<Record<string, Array<Product>>>((acc, product) => {
    const categoryName = product.category?.name ?? 'Uncategorized';
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (acc[categoryName]) {
      acc[categoryName].push(product);
    } else {
      acc[categoryName] = [product];
    }
    return acc;
  }, {});

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <Accordion title="Prestations" isOpenDefault>
        <div className="flex flex-col gap-2">
          {services
            .filter((service) => !service.has_products && service.show_public)
            .map((service) => (
              <button
                key={service.id}
                type="button"
                className="flex items-center justify-between rounded-lg border border-amber-200/60 bg-white/50 p-3 text-left transition-colors hover:border-amber-400 hover:bg-amber-50/50"
                onClick={() => onSelect(service)}
              >
                <div className="mr-3">
                  <h3 className="text-sm font-semibold text-gray-900 md:text-base">
                    {service.name}
                  </h3>
                  <p className="mt-0.5 text-xs text-gray-500">{service.duration} min</p>
                </div>
                <span className="text-sm font-semibold whitespace-nowrap text-amber-900 md:text-base">
                  {service.price ? `${service.price} €` : 'Gratuit'}
                </span>
              </button>
            ))}
        </div>
      </Accordion>

      {Object.entries(productsByCategory).map(([category, categoryProducts], key) => (
        <Accordion key={category} title={category} isOpenDefault={key === 0}>
          <div className="flex flex-col gap-2">
            {categoryProducts
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((product) => (
                <button
                  key={product.id}
                  type="button"
                  className="flex items-center justify-between rounded-lg border border-amber-200/60 bg-white/50 p-3 text-left transition-colors hover:border-amber-400 hover:bg-amber-50/50"
                  onClick={() => onSelect(services.find((s) => s.has_products) as Service, product)}
                >
                  <div className="mr-3">
                    <h3 className="text-sm font-semibold text-gray-900 md:text-base">
                      {product.name}
                    </h3>
                    {product.description ? (
                      <p className="mt-0.5 text-xs text-gray-500">{product.description}</p>
                    ) : null}
                  </div>
                  <span className="text-sm font-semibold whitespace-nowrap text-amber-900 md:text-base">
                    {product.price} €
                  </span>
                </button>
              ))}
          </div>
        </Accordion>
      ))}
    </div>
  );
};

export default ProductSelection;
